import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Button, Modal, Form } from 'react-bootstrap';
import { colors } from '../../brand-colors';

function Calendars() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, list
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'normal',
    category: '',
  });

  // Business Tasks state (from user_tasks table)
  const [businessTasks, setBusinessTasks] = useState([]);
  const [showBusinessTaskModal, setShowBusinessTaskModal] = useState(false);
  const [editingBusinessTask, setEditingBusinessTask] = useState(null);
  const [newBusinessTask, setNewBusinessTask] = useState({
    title: '',
    description: '',
    category: 'operations',
    priority: 'medium',
    target_date: '',
  });

  useEffect(() => {
    fetchTasks();
    fetchBusinessTasks();
  }, []);

  const fetchBusinessTasks = async () => {
    try {
      const response = await fetch('/api/tasks/list?status=active', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessTasks(data.tasks || []);
      }
    } catch (err) {
      console.error('Error fetching business tasks:', err);
    }
  };

  const createBusinessTask = async () => {
    try {
      const response = await fetch('/api/tasks/create', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newBusinessTask,
          target_date: newBusinessTask.target_date || null,
          source: 'manual',
        }),
      });
      if (response.ok) {
        setShowBusinessTaskModal(false);
        setNewBusinessTask({ title: '', description: '', category: 'operations', priority: 'medium', target_date: '' });
        fetchBusinessTasks();
      }
    } catch (err) {
      console.error('Error creating business task:', err);
    }
  };

  const updateBusinessTask = async (taskId, updates) => {
    try {
      await fetch(`/api/tasks/update/${taskId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      fetchBusinessTasks();
    } catch (err) {
      console.error('Error updating business task:', err);
    }
  };

  const deleteBusinessTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await fetch(`/api/tasks/delete/${taskId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchBusinessTasks();
    } catch (err) {
      console.error('Error deleting business task:', err);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/calendar/tasks', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch tasks');
      const data = await response.json();
      setTasks(data.tasks || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async () => {
    try {
      const response = await fetch('/api/calendar/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newTask,
          task_type: 'human',
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setShowAddModal(false);
        setNewTask({ title: '', description: '', due_date: '', priority: 'normal', category: '' });
        fetchTasks();
      } else {
        console.error('Server error:', data);
        alert('Error creating task: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error creating task:', err);
      alert('Error creating task: ' + err.message);
    }
  };

  const completeTask = async (taskId) => {
    try {
      await fetch(`/api/calendar/tasks/${taskId}/complete`, {
        method: 'POST',
        credentials: 'include',
      });
      fetchTasks();
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const openEditModal = (task) => {
    // Don't allow editing promotion events
    if (String(task.id).startsWith('promo_')) {
      alert('Promotion events are managed in the Promotions section of Campaign HQ.');
      return;
    }
    setEditingTask({
      ...task,
      due_date: task.due_date ? task.due_date.slice(0, 16) : '', // Format for datetime-local
    });
    setShowEditModal(true);
  };

  const updateTask = async () => {
    if (!editingTask) return;
    try {
      const response = await fetch(`/api/calendar/tasks/${editingTask.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingTask.title,
          description: editingTask.description,
          due_date: editingTask.due_date,
          priority: editingTask.priority,
          category: editingTask.category,
        }),
      });
      if (response.ok) {
        setShowEditModal(false);
        setEditingTask(null);
        fetchTasks();
      } else {
        const data = await response.json();
        alert('Error updating task: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating task:', err);
      alert('Error updating task: ' + err.message);
    }
  };

  const deleteTask = async (taskId) => {
    // Don't allow deleting promotion events
    if (String(taskId).startsWith('promo_')) {
      alert('Promotion events are managed in the Promotions section of Campaign HQ.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    try {
      const response = await fetch(`/api/calendar/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        fetchTasks();
      } else {
        const data = await response.json();
        alert('Error deleting task: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Error deleting task: ' + err.message);
    }
  };

  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Add empty slots for days before the first of the month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }

    return days;
  }, [selectedDate]);

  // Group tasks by date
  const tasksByDate = useMemo(() => {
    const grouped = {};
    tasks.forEach(task => {
      if (task.due_date) {
        const dateKey = task.due_date.split('T')[0];
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(task);
      }
    });
    return grouped;
  }, [tasks]);

  // Get tasks for a specific day
  const getTasksForDay = (date) => {
    if (!date) return [];
    const dateKey = date.toISOString().split('T')[0];
    return tasksByDate[dateKey] || [];
  };

  // Upcoming tasks (next 7 days)
  const upcomingTasks = useMemo(() => {
    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return tasks
      .filter(t => {
        if (!t.due_date) return false;
        const dueDate = new Date(t.due_date);
        return dueDate >= now && dueDate <= nextWeek && t.status !== 'completed';
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  }, [tasks]);

  const monthName = selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const navigateMonth = (direction) => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + direction, 1));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return colors.redDarkest;
      case 'high': return '#dc3545';
      case 'normal': return colors.goldMid;
      case 'low': return colors.greyMid;
      default: return colors.greyMid;
    }
  };

  const getTaskTypeIcon = (taskType) => {
    switch (taskType) {
      case 'event': return '🎯';
      case 'human': return '✓';
      default: return '•';
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading your calendar...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">Error: {error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Calendars & Tasks</h1>
          <p className="text-muted mb-0">
            Track your tasks, deadlines, and upcoming promotions.
          </p>
        </div>
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={() => {
            // Pre-fill due date with today at noon
            const today = new Date();
            today.setHours(12, 0, 0, 0);
            const dateStr = today.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
            setNewTask({ ...newTask, due_date: dateStr });
            setShowAddModal(true);
          }}
        >
          + Add Task
        </Button>
      </div>

      <Row>
        {/* Calendar View */}
        <Col lg={8} className="mb-4">
          <Card className="shadow">
            <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <Button variant="link" style={{ color: colors.goldLight }} onClick={() => navigateMonth(-1)}>
                ← Prev
              </Button>
              <h5 className="mb-0">{monthName}</h5>
              <Button variant="link" style={{ color: colors.goldLight }} onClick={() => navigateMonth(1)}>
                Next →
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {/* Day headers */}
              <div className="d-flex border-bottom" style={{ backgroundColor: colors.greyLightest }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="flex-fill text-center py-2 small fw-bold" style={{ color: colors.greyDark }}>
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="d-flex flex-wrap">
                {calendarDays.map((day, index) => {
                  const dayTasks = getTasksForDay(day);
                  const isToday = day && day.toDateString() === new Date().toDateString();
                  return (
                    <div
                      key={index}
                      style={{
                        width: '14.28%',
                        minHeight: '100px',
                        borderRight: '1px solid #dee2e6',
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: isToday ? '#fff8e6' : 'white',
                        padding: '4px',
                      }}
                    >
                      {day && (
                        <>
                          <div className="text-end small" style={{ color: isToday ? colors.goldDark : colors.greyMid, fontWeight: isToday ? '600' : '400' }}>
                            {day.getDate()}
                          </div>
                          <div style={{ fontSize: '0.7rem' }}>
                            {dayTasks.slice(0, 3).map((task, i) => (
                              <div
                                key={i}
                                className="text-truncate mb-1 px-1 rounded calendar-task-item"
                                style={{
                                  backgroundColor: task.task_type === 'event' ? colors.goldLight : (task.status === 'completed' ? '#d4edda' : '#f8f9fa'),
                                  color: colors.greyDarkest,
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                                title={`${task.title} - Click to edit`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditModal(task);
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.02)';
                                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                {getTaskTypeIcon(task.task_type)} {task.title}
                              </div>
                            ))}
                            {dayTasks.length > 3 && (
                              <div className="text-muted text-center">+{dayTasks.length - 3} more</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Upcoming Tasks Sidebar */}
        <Col lg={4}>
          <Card className="shadow-sm mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h5 className="mb-0">Upcoming Tasks</h5>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {upcomingTasks.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No upcoming tasks this week
                </div>
              ) : (
                upcomingTasks.map((task) => (
                  <div
                    key={task.id}
                    className="d-flex align-items-start p-3 border-bottom"
                    style={{ backgroundColor: task.status === 'completed' ? '#f8fff8' : 'white' }}
                  >
                    <div
                      className="me-3"
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: `2px solid ${getPriorityColor(task.priority)}`,
                        cursor: task.task_type === 'human' ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: task.status === 'completed' ? '#28a745' : 'white',
                        color: task.status === 'completed' ? 'white' : 'transparent',
                        fontSize: '0.8rem',
                        flexShrink: 0,
                      }}
                      onClick={() => task.task_type === 'human' && task.status !== 'completed' && completeTask(task.id)}
                      title={task.task_type === 'human' ? 'Click to complete' : ''}
                    >
                      {task.status === 'completed' ? '✓' : ''}
                    </div>
                    <div className="flex-grow-1">
                      <div style={{
                        fontWeight: '500',
                        textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                        color: task.status === 'completed' ? colors.greyMid : colors.greyDarkest,
                      }}>
                        {task.task_type === 'event' && '🎯 '}
                        {task.title}
                      </div>
                      <small className="text-muted">
                        {new Date(task.due_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        {task.category && <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.65rem' }}>{task.category}</Badge>}
                      </small>
                    </div>
                    {task.task_type === 'human' && (
                      <div className="ms-2 d-flex flex-column" style={{ fontSize: '0.75rem' }}>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-primary"
                          style={{ fontSize: '0.75rem' }}
                          onClick={() => openEditModal(task)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-danger"
                          style={{ fontSize: '0.75rem' }}
                          onClick={() => deleteTask(task.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </Card.Body>
          </Card>

          {/* Quick Stats */}
          <Card className="shadow-sm">
            <Card.Body>
              <h6 className="text-muted mb-3">This Month</h6>
              <Row className="text-center">
                <Col>
                  <div className="fw-bold fs-4" style={{ color: colors.goldDark }}>
                    {tasks.filter(t => t.task_type === 'human' && t.status !== 'completed').length}
                  </div>
                  <small className="text-muted">Pending</small>
                </Col>
                <Col>
                  <div className="fw-bold fs-4" style={{ color: '#28a745' }}>
                    {tasks.filter(t => t.status === 'completed').length}
                  </div>
                  <small className="text-muted">Completed</small>
                </Col>
                <Col>
                  <div className="fw-bold fs-4" style={{ color: colors.redDarkest }}>
                    {tasks.filter(t => t.task_type === 'event').length}
                  </div>
                  <small className="text-muted">Events</small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Business Tasks Section */}
      <Row className="mt-4">
        <Col>
          <Card className="shadow">
            <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div>
                <h5 className="mb-0">Business Tasks</h5>
                <small style={{ color: colors.goldMid }}>
                  Long-term improvements & "planned for future" items
                </small>
              </div>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => {
                  setEditingBusinessTask(null);
                  setNewBusinessTask({ title: '', description: '', category: 'operations', priority: 'medium', target_date: '' });
                  setShowBusinessTaskModal(true);
                }}
              >
                + Add Task
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {businessTasks.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
                  <p className="mb-2">No business tasks yet</p>
                  <small>Tasks from "planned for future" answers and manual entries will appear here.</small>
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {businessTasks.map((task) => {
                    const daysOld = task.created_at
                      ? Math.floor((new Date() - new Date(task.created_at)) / (1000 * 60 * 60 * 24))
                      : 0;

                    return (
                      <div
                        key={task.id}
                        className="d-flex align-items-start p-3 border-bottom"
                        style={{ backgroundColor: task.status === 'in_progress' ? '#fff8e6' : 'white' }}
                      >
                        {/* Complete checkbox */}
                        <div
                          className="me-3"
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            border: `2px solid ${task.priority === 'high' ? '#dc3545' : task.priority === 'low' ? colors.greyMid : colors.goldMid}`,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'white',
                            flexShrink: 0,
                          }}
                          onClick={() => updateBusinessTask(task.id, { status: 'completed' })}
                          title="Mark as complete"
                        >
                          {task.status === 'in_progress' && (
                            <div style={{ width: '10px', height: '10px', backgroundColor: colors.goldMid, borderRadius: '2px' }} />
                          )}
                        </div>

                        {/* Task content */}
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <strong style={{ color: colors.greyDarkest }}>{task.title}</strong>
                            <Badge
                              bg={task.priority === 'high' ? 'danger' : task.priority === 'low' ? 'secondary' : 'warning'}
                              className="ms-2"
                              style={{ fontSize: '0.65rem' }}
                            >
                              {task.priority}
                            </Badge>
                            {task.category && (
                              <Badge bg="info" className="ms-1" style={{ fontSize: '0.65rem' }}>
                                {task.category}
                              </Badge>
                            )}
                            {task.source && task.source !== 'manual' && (
                              <Badge bg="light" text="dark" className="ms-1" style={{ fontSize: '0.65rem' }}>
                                auto: {task.source}
                              </Badge>
                            )}
                          </div>
                          {task.description && (
                            <div className="text-muted small mb-1">{task.description}</div>
                          )}
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <small className="text-muted">
                              Added {task.created_at ? new Date(task.created_at).toLocaleDateString() : 'recently'}
                            </small>
                            <small style={{ color: task.target_date ? colors.goldDark : colors.greyMid }}>
                              • Due: {task.target_date ? new Date(task.target_date).toLocaleDateString() : 'Open'}
                            </small>
                            {daysOld > 30 && (
                              <small className="text-danger">
                                ⚠️ {daysOld} days old
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="ms-2 d-flex flex-column" style={{ fontSize: '0.75rem' }}>
                          {task.status === 'pending' && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-warning"
                              style={{ fontSize: '0.75rem' }}
                              onClick={() => updateBusinessTask(task.id, { status: 'in_progress' })}
                              title="Start working on this"
                            >
                              Start
                            </Button>
                          )}
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => {
                              setEditingBusinessTask(task);
                              setNewBusinessTask({
                                title: task.title,
                                description: task.description || '',
                                category: task.category || 'operations',
                                priority: task.priority || 'medium',
                                target_date: task.target_date ? task.target_date.slice(0, 10) : '',
                              });
                              setShowBusinessTaskModal(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => deleteBusinessTask(task.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
            {businessTasks.length > 0 && (
              <Card.Footer className="bg-light">
                <Row className="text-center small">
                  <Col>
                    <strong style={{ color: '#dc3545' }}>{businessTasks.filter(t => t.priority === 'high').length}</strong>
                    <span className="text-muted ms-1">High Priority</span>
                  </Col>
                  <Col>
                    <strong style={{ color: colors.goldDark }}>{businessTasks.filter(t => t.status === 'in_progress').length}</strong>
                    <span className="text-muted ms-1">In Progress</span>
                  </Col>
                  <Col>
                    <strong>{businessTasks.length}</strong>
                    <span className="text-muted ms-1">Total Active</span>
                  </Col>
                </Row>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Add Task Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>Add New Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Task title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional description"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={newTask.due_date}
                onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={newTask.category}
                    onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="content">Content</option>
                    <option value="approval">Approval</option>
                    <option value="marketing">Marketing</option>
                    <option value="sales">Sales</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={createTask}
            disabled={!newTask.title || !newTask.due_date}
          >
            Add Task
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Task Modal */}
      <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setEditingTask(null); }}>
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTask && (
            <Form onSubmit={(e) => e.preventDefault()}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Task title"
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Optional description"
                  value={editingTask.description || ''}
                  onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={editingTask.due_date}
                  onChange={(e) => setEditingTask({ ...editingTask, due_date: e.target.value })}
                />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={editingTask.priority}
                      onChange={(e) => setEditingTask({ ...editingTask, priority: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      value={editingTask.category || ''}
                      onChange={(e) => setEditingTask({ ...editingTask, category: e.target.value })}
                    >
                      <option value="">Select...</option>
                      <option value="content">Content</option>
                      <option value="approval">Approval</option>
                      <option value="marketing">Marketing</option>
                      <option value="sales">Sales</option>
                      <option value="admin">Admin</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={() => {
              if (editingTask) {
                deleteTask(editingTask.id);
                setShowEditModal(false);
                setEditingTask(null);
              }
            }}
          >
            Delete Task
          </Button>
          <Button variant="secondary" onClick={() => { setShowEditModal(false); setEditingTask(null); }}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={updateTask}
            disabled={!editingTask?.title || !editingTask?.due_date}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Business Task Modal (Add/Edit) */}
      <Modal show={showBusinessTaskModal} onHide={() => { setShowBusinessTaskModal(false); setEditingBusinessTask(null); setNewBusinessTask({ title: '', description: '', category: 'operations', priority: 'medium', target_date: '' }); }}>
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>{editingBusinessTask ? 'Edit Business Task' : 'Add Business Task'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Form.Group className="mb-3">
              <Form.Label>Task Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="What needs to be done?"
                value={newBusinessTask.title}
                onChange={(e) => setNewBusinessTask({ ...newBusinessTask, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Additional details..."
                value={newBusinessTask.description}
                onChange={(e) => setNewBusinessTask({ ...newBusinessTask, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Due Date (optional)</Form.Label>
              <Form.Control
                type="date"
                value={newBusinessTask.target_date}
                onChange={(e) => setNewBusinessTask({ ...newBusinessTask, target_date: e.target.value })}
              />
              <Form.Text className="text-muted">
                Leave blank for open-ended tasks
              </Form.Text>
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={newBusinessTask.priority}
                    onChange={(e) => setNewBusinessTask({ ...newBusinessTask, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    High priority tasks show in the War Room
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={newBusinessTask.category}
                    onChange={(e) => setNewBusinessTask({ ...newBusinessTask, category: e.target.value })}
                  >
                    <option value="operations">Operations</option>
                    <option value="marketing">Marketing</option>
                    <option value="products">Products</option>
                    <option value="team">Team</option>
                    <option value="finance">Finance</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowBusinessTaskModal(false); setEditingBusinessTask(null); setNewBusinessTask({ title: '', description: '', category: 'operations', priority: 'medium', target_date: '' }); }}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={async () => {
              if (editingBusinessTask) {
                await updateBusinessTask(editingBusinessTask.id, newBusinessTask);
                setShowBusinessTaskModal(false);
                setEditingBusinessTask(null);
              } else {
                await createBusinessTask();
              }
            }}
            disabled={!newBusinessTask.title}
          >
            {editingBusinessTask ? 'Save Changes' : 'Add Task'}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default Calendars;
